import { isNil } from './fn';

/**
 * Check whether a value is a non-null object that can be read as a record.
 * Arrays and class instances pass too; add an `Array.isArray` check when they must be excluded.
 */
export function isRecord(value: unknown): value is Record<PropertyKey, unknown> {
	return typeof value === 'object' && value !== null;
}

export function pick<T, K extends keyof T>(
	obj: T,
	keys: K[],
): Pick<T, K> {
	return keys.reduce((acc, key) => {
		if (obj[key] !== undefined) {
			acc[key] = obj[key];
		}

		return acc;
	}, {} as Pick<T, K>);
}

export function omit<Data extends object, Keys extends keyof Data>(data: Data, keys: Keys[]): Omit<Data, Keys> {
	const result = { ...data };

	for (const key of keys) {
		delete result[key];
	}

	return result as Omit<Data, Keys>;
}

/**
 * Returns a shallow copy without properties whose value is `undefined`.
 */
export function omitUndefined<T extends Record<string, any>>(object: T): { [K in keyof T]: Exclude<T[K], undefined> } {
	return Object.fromEntries(
		Object.entries(object).filter(([, value]) => value !== undefined),
	) as { [K in keyof T]: Exclude<T[K], undefined> };
}

export function get(object: Record<string, any> | undefined, path: (string | number)[] | string, defaultValue?: any): any {
	if (typeof path === 'string') {
		path = path.split('.').map((key) => {
			const numKey = Number(key);
			return Number.isNaN(numKey) ? key : numKey;
		});
	}

	let result: any = object;

	for (const key of path) {
		if (isNil(result)) {
			return defaultValue;
		}

		result = result[key];
	}

	return result !== undefined ? result : defaultValue;
}

export function set(object: Record<string, any>, path: (string | number)[] | string, value: any) {
	if (typeof path === 'string') {
		path = path.split('.').map((key) => {
			const numKey = Number(key);
			return Number.isNaN(numKey) ? key : numKey;
		});
	}

	path.reduce((acc, key, i) => {
		if (acc[key] === undefined)
			acc[key] = {};
		if (i === path.length - 1)
			acc[key] = value;
		return acc[key];
	}, object);
}

export function mapValues<T extends object, K extends keyof T, V>(object: T, iteratee: (value: T[K], key: K, object: T) => V): Record<K, V> {
	const result = {} as Record<K, V>;
	const keys = Object.keys(object);

	for (const key_ of keys) {
		const key = key_ as K;
		const value = object[key];

		result[key] = iteratee(value, key, object);
	}

	return result;
}

export function mapKeys<T extends object, K extends PropertyKey>(object: T, iteratee: (value: T[keyof T], key: keyof T, object: T) => K): Record<K, T[keyof T]> {
	const result = {} as Record<K, T[keyof T]>;

	for (const key in object) {
		if (Object.prototype.hasOwnProperty.call(object, key)) {
			result[iteratee(object[key], key, object)] = object[key];
		}
	}

	return result;
}

export function isObjectEmpty(object: Record<string, any>): boolean {
	return Object.keys(object).length === 0 || Object.values(object).every((value) => isNil(value));
}
